import { AnimatePresence, motion } from "framer-motion";
import compact from "lodash/compact";
import first from "lodash/first";
import last from "lodash/last";
import map from "lodash/map";
import React, { FunctionComponent, ReactElement, useRef } from "react";
import { useIntl } from "react-intl";
import useCustomizationContext from "../../../../hooks/useCustomizationContext";
import { ReactComponent as IconLayers } from "../../../../icons/layers.svg";
import { ReactComponent as IconMinus } from "../../../../icons/minus-square.svg";
import useElementClientBoundingRect from "../../../../lib/useElementClientBoundingRect";
import { Panel } from "../../../../states/customization";
import { Unit } from "../../../../types/customization/box";
import { lowQualityPicturesByPage, lengthMmToPx } from "../../../../utils/customization/box";
import { firstMatchingCondition } from "../../../../utils/customization/condition";
import { maxPageReached } from "../../../../utils/customization/configuration";
import {
  getCoverFilteredPages,
  getNoCoverDefinitions,
} from "../../../../utils/customization/cover";
import { isMobileSize } from "../../../../utils/customization/device";
import {
  findPageAndDefinition,
  findPresentationDefinition,
  findPrintQuantity,
  findValidPageDefinitionsToAdd,
} from "../../../../utils/customization/finders";
import { buildRotationPagesMapping, getPageLabel } from "../../../../utils/customization/page";
import { canSelectPageDefinitions } from "../../../../utils/customization/pageDefinition";
import Button from "../../../atoms/Button";
import LowQualityIndicator from "../../../atoms/customization/LowQualityIndicator";
import AddPage from "../AddPage";
import Page from "../Page";
import PageBar from "../PageBar";
import PagePresentation from "../PagePresentation";
import PageQuantity from "../PageQuantity";
import ShapeBorders from "../ShapeBorders";
import TranslationByType from "../TranslationByType";
import "./style.scss";

const motionItemAnimate = { opacity: 1, y: 0 };
const motionItemExit = { opacity: 0, y: -20 };
const motionItemInitial = { opacity: 0, y: 20 };
const motionItemSpring = {
  type: "spring",
  damping: 10,
  stiffness: 100,
};

interface Props {
  children?: ReactElement;
  onLowQualityWarning?: (PicturesCount: number) => void;
  ppmm: number;
}

const PageList: FunctionComponent<Props> = ({ children, onLowQualityWarning, ppmm }) => {
  const {
    content,
    configuration,
    dispatch,
    selection,
    template,
    ui: { panel },
  } = useCustomizationContext();
  const intl = useIntl();

  const pageListRef = useRef<HTMLDivElement>(null);
  const pageListBoundingRect = useElementClientBoundingRect(pageListRef);

  let mainLowQualityPicturesCount = 0;

  const noCoverPageDefinitions = getNoCoverDefinitions(template.pageDefinitions);
  const noCoverPageDefinitionsKeys = noCoverPageDefinitions.map((pageDefinition) => {
    return pageDefinition.key;
  });
  const contentPages = content.pages.filter((page) => {
    return noCoverPageDefinitionsKeys.includes(page.pageDefinitionKey);
  });

  const listItems = map(contentPages, (page, pageIndex) => {
    const pageKey = page.key;

    const { pageDefinition } = findPageAndDefinition(pageKey, template, content);
    let height = pageDefinition.height;
    let width = pageDefinition.width;

    const presentation = firstMatchingCondition(
      pageDefinition.presentations,
      template,
      configuration
    );
    if (presentation) {
      const presentationDefinition = findPresentationDefinition(
        presentation?.presentationDefinitionKey,
        template
      );
      height = presentationDefinition.height;
      width = presentationDefinition.width;
    }

    const hasOnePageMax = configuration && configuration.pages && configuration.pages.max === 1;

    const borderElements = hasOnePageMax === false && panel.key === pageKey && (
      <div className="PageList-item-borders">
        <ShapeBorders
          pageDefinition={pageDefinition}
          placementValues={{
            height: lengthMmToPx(height, ppmm),
            left: { unit: Unit.PX, value: 0 },
            top: { unit: Unit.PX, value: 0 },
            width: lengthMmToPx(width, ppmm),
          }}
          template={template}
          type="border"
        />
      </div>
    );

    const renderedPage = (
      <PagePresentation pageDefinitionKey={page.pageDefinitionKey} ppmm={ppmm} template={template}>
        <Page content={content} pageKey={pageKey} ppmm={ppmm} template={template} />
      </PagePresentation>
    );

    let printElements;
    if (content.templateType === "print") {
      const { quantity } = findPrintQuantity(pageKey, content);

      printElements =
        quantity >= 2 ? (
          <div className="PageList-item-stack">
            {renderedPage}
            {borderElements}
          </div>
        ) : null;
    }

    const lowQualityPicturesCount = lowQualityPicturesByPage(
      page.pageDefinitionKey,
      page.editablePictures,
      selection,
      template
    );
    if (onLowQualityWarning && lowQualityPicturesCount > 0) {
      mainLowQualityPicturesCount += lowQualityPicturesCount;
      onLowQualityWarning(mainLowQualityPicturesCount);
    } else if (onLowQualityWarning && mainLowQualityPicturesCount === 0) {
      onLowQualityWarning(0);
    }

    const hasColors = pageDefinition.backgroundColorKeys.length > 1;
    const hasEditableTexts = pageDefinition.elementDefinitions.find(
      (definition) => definition.type === "editable-text"
    );
    const hasFonts = template.fontDefinitions.length > 1 && !!hasEditableTexts;
    const hasLayouts = canSelectPageDefinitions(template, content, {
      type: pageDefinition.type,
      filterType: pageDefinition.filterType,
    });
    const hiddenWhenUseless =
      isMobileSize() === false && panel.current === Panel.EDITPAGE && panel.key === pageKey;

    const rotationPairsMapping = buildRotationPagesMapping(template.pageDefinitions);
    const isRotationPair =
      hasLayouts &&
      Object.keys(rotationPairsMapping).length === 2 &&
      rotationPairsMapping[pageDefinition.key];

    const displayPanelCondition =
      !hiddenWhenUseless && ((hasLayouts && !isRotationPair) || hasColors || hasFonts);

    const pageBarElement = (
      <PageBar
        content={content}
        displayPanelCondition={displayPanelCondition}
        isRotationPair={isRotationPair}
        pageKey={pageKey}
      />
    );
    const pageQuantityElement = <PageQuantity content={content} pageKey={pageKey} />;

    const { label, translationId } = getPageLabel(configuration, template.templateType);
    const pageTitle = label ? label : intl.formatMessage({ id: translationId });

    const handleOnClickOptions = () => {
      if (displayPanelCondition) {
        dispatch({
          key: pageKey,
          panel: Panel.EDITPAGE,
          type: "setPanel",
        });
      }
    };

    return {
      item: (
        <>
          <div className="PageList-number" onClick={handleOnClickOptions}>
            {hasOnePageMax === false && (
              <span className={panel.key === pageKey ? "PageList-number--active" : ""}>
                {pageTitle} {pageIndex + 1}
              </span>
            )}
            {lowQualityPicturesCount > 0 && (
              <LowQualityIndicator
                label={intl.formatMessage({
                  id: "kustom_PageList_LowQuality_some",
                })}
              />
            )}
          </div>
          <div className="PageList-renderedPage">
            {renderedPage}
            {printElements}
            {borderElements}
          </div>
          {pageQuantityElement}
          {pageBarElement}
        </>
      ),
      key: pageKey,
    };
  });

  const animatedListItems = compact(
    map(listItems, (listItem, pageIndex) => {
      if (template.templateType === "book" && template.pageMode === "single") {
        if (pageIndex > 0 && pageIndex % 2 !== 0) {
          return null;
        }
        return (
          <motion.div
            animate={motionItemAnimate}
            exit={motionItemExit}
            initial={motionItemInitial}
            layout
            transition={motionItemSpring}
            key={listItem.key}
            className="PageList-item PageList-item-double"
          >
            <div className="PageList-item-even">{listItem.item}</div>
            <div className="PageList-item-odd">
              {listItems[pageIndex + 1] && listItems[pageIndex + 1].item}
            </div>
          </motion.div>
        );
      } else {
        return (
          <motion.div
            animate={motionItemAnimate}
            exit={motionItemExit}
            initial={motionItemInitial}
            layout
            transition={motionItemSpring}
            key={listItem.key}
            className="PageList-item"
          >
            {listItem.item}
          </motion.div>
        );
      }
    })
  );

  let addPageDefinitionKey: string | undefined;
  const noCoverContentPages = getCoverFilteredPages(content.pages, noCoverPageDefinitions);
  const addNextFilteredPageDefinitions = findValidPageDefinitionsToAdd(template, content);
  const addNextFilteredPageDefinitionsKeys = addNextFilteredPageDefinitions.map(
    (definition) => definition.key
  );
  const lastPage = last(noCoverContentPages);
  if (lastPage && addNextFilteredPageDefinitionsKeys.includes(lastPage.pageDefinitionKey)) {
    addPageDefinitionKey = lastPage.pageDefinitionKey;
  } else {
    const firstTemplatePageDefinition = first(addNextFilteredPageDefinitions);
    if (firstTemplatePageDefinition) {
      addPageDefinitionKey = firstTemplatePageDefinition.key;
    }
  }

  const minPages = (configuration && configuration.pages && configuration.pages.min) || 1;
  const packIncrement = configuration && configuration.pages && configuration.pages.increment;
  const hasPack = (packIncrement && packIncrement > 1) || false;
  const hasAdditionnalPages =
    packIncrement && noCoverContentPages.length >= minPages + packIncrement;
  const hasRemoveButton =
    template.templateType === "book" || (template.templateType === "mural" && hasPack);
  const removeButtonOnClickHandler = () => {
    dispatch({
      mode: "deletion",
      opened: true,
      type: "setOrganizePageList",
    });
  };

  let bookHeaderWidth = 0;
  if (template.templateType === "book") {
    const pageDefinitionsWithoutCover = getNoCoverDefinitions(template.pageDefinitions);
    let listItemWidth = pageDefinitionsWithoutCover[0].width.value * ppmm;
    if (pageDefinitionsWithoutCover[0].type === "single-page") {
      listItemWidth = listItemWidth * 2;
    }
    const horizontalMargin = 72;
    const containerWidth =
      (pageListBoundingRect ? pageListBoundingRect.width : window.innerWidth) - horizontalMargin;
    const nbBooksByLine = Math.floor(containerWidth / listItemWidth);
    bookHeaderWidth = listItemWidth * nbBooksByLine + 24 * (nbBooksByLine - 1); // 24 = pages margin
  }

  return (
    <div className="PageList" ref={pageListRef}>
      {content.templateType === "book" && (
        <div className="PageList-header" style={{ width: bookHeaderWidth }}>
          <div className="PageList-header-left">
            {configuration && configuration.pageName
              ? configuration.pageName.plural
              : intl.formatMessage({ id: "kustom_PageList_Pages__book" })}
          </div>
          <div className="PageList-header-right">
            <Button
              type="primary-darker-transparent"
              onClick={() =>
                dispatch({
                  type: "setOrganizePageList",
                  mode: "ordering",
                  opened: true,
                })
              }
            >
              <IconLayers height="16" width="16" />
              {intl.formatMessage({ id: "kustom_PageList_Reorder" })}
            </Button>
          </div>
        </div>
      )}

      <AnimatePresence>{animatedListItems}</AnimatePresence>
      <div className="PageList-pages-buttons">
        {addPageDefinitionKey &&
          !maxPageReached(content, configuration, template.pageDefinitions) && (
            <AddPage pageDefinitionKey={addPageDefinitionKey} ppmm={ppmm} />
          )}
        {hasRemoveButton && (
          <Button onClick={removeButtonOnClickHandler} disabled={!hasAdditionnalPages} type="foggy">
            <IconMinus height={20} width={20} />
            <TranslationByType
              pageName={configuration && configuration.pageName}
              pageNameChoice="plural"
              templateType={template.templateType}
              translationBase="kustom_PageList_RemoveButton"
            />
          </Button>
        )}
      </div>
      {children}
    </div>
  );
};

export default PageList;
